import { Link, useParams, Navigate } from 'react-router-dom';
import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { FadeUp, RevealLines } from '../components/Reveal';
import { ARTICLES, ARTICLES_BY_SLUG } from '../data/articles';

const COUNTRY_PATH = {
  'UK': '/uk', 'USA': '/usa', 'Switzerland': '/switzerland', 'Germany': '/germany',
  'UAE': '/uae', 'Netherlands': '/netherlands', 'France': '/france', 'Spain': '/spain',
  'Greece': '/greece',
};

// Renders article text with inline internal links written as [label](/path).
function renderText(text) {
  return text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) {
      return (
        <Link key={i} to={m[2]}
          className="underline decoration-white/30 underline-offset-4 hover:text-white hover:decoration-white transition-colors">
          {m[1]}
        </Link>
      );
    }
    return part;
  });
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = ARTICLES_BY_SLUG[slug];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll();
  const progress = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  if (!article) return <Navigate to="/articles" replace />;

  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  // JSON-LD Article schema for SEO
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    image: article.ogImage,
    author: { '@type': 'Organization', name: 'Studio Legale Belliardo' },
    publisher: {
      '@type': 'LegalService',
      name: 'Studio Legale Belliardo',
      url: 'https://belliardo-international.com',
    },
  };

  return (
    <div ref={ref} className="relative bg-ink-950 text-white" data-testid={`article-${article.slug}`}>
      <Helmet>
        <title>{`${article.title} — Studio Belliardo Journal`}</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={article.ogImage} />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:section" content={article.country} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={article.ogImage} />
        <link rel="canonical" href={`/articles/${article.slug}`} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Nav />

      {/* Progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-oxblood origin-left z-[60]"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Hero */}
      <section className="pt-40 md:pt-52 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <FadeUp>
            <Link to="/articles" data-testid="article-back"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white mb-10">
              ← Journal
            </Link>
          </FadeUp>
          <FadeUp delay={0.05}>
            <div className="flex items-center gap-4 text-white/50 mb-8 font-mono text-[10px] uppercase tracking-[0.3em]">
              <span>— {article.country}</span>
              <span className="text-white/25">·</span>
              <span>{article.date}</span>
              <span className="text-white/25">·</span>
              <span>{article.read} min read</span>
            </div>
          </FadeUp>
          <h1 className="font-serif font-light text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
            <RevealLines lines={[article.title]} delay={0.15} />
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-10 text-xl md:text-2xl text-white/70 leading-snug font-serif italic max-w-3xl font-light">
              {article.subtitle}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Feature image */}
      <FadeUp>
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 mb-16 md:mb-24">
          <motion.div
            className="clip-frame-tr overflow-hidden bg-ink-800 aspect-[16/8]"
            initial={{ opacity: 0, scale: 1.02 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <img src={article.ogImage} alt={article.title}
              className="w-full h-full object-cover grayscale contrast-110 brightness-90" />
          </motion.div>
        </div>
      </FadeUp>

      {/* Body */}
      <article className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[760px] px-6 md:px-10 space-y-8">
          {article.body.map((block, i) => {
            if (block.h) return (
              <FadeUp key={i} delay={0.05}>
                <h2 className="font-serif text-3xl md:text-4xl text-white font-light leading-tight tracking-tight pt-8 border-t border-white/10">
                  {block.h}
                </h2>
              </FadeUp>
            );
            if (block.p) return (
              <FadeUp key={i} delay={0.03}>
                <p className="text-white/80 text-lg leading-relaxed">{renderText(block.p)}</p>
              </FadeUp>
            );
            if (block.list) return (
              <FadeUp key={i} delay={0.03}>
                <ul className="space-y-3">
                  {block.list.map((item, j) => (
                    <li key={j} className="flex gap-4 text-white/75 text-lg leading-relaxed">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 pt-2 w-8 flex-shrink-0">0{j + 1}</span>
                      <span>{renderText(item)}</span>
                    </li>
                  ))}
                </ul>
              </FadeUp>
            );
            if (block.table) return (
              <FadeUp key={i} delay={0.03}>
                <div className="overflow-x-auto border border-white/10" data-testid={`article-table-${i}`}>
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-white/10 bg-ink-900">
                        {block.table.head.map((h, j) => (
                          <th key={j} className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 font-normal">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {block.table.rows.map((row, j) => (
                        <tr key={j} className="border-b border-white/10 last:border-0">
                          {row.map((cell, k) => (
                            <td key={k} className="px-5 py-4 text-white/75 text-base leading-relaxed align-top">{renderText(cell)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </FadeUp>
            );
            return null;
          })}

          {/* Disclaimer */}
          <div className="pt-12 mt-12 border-t border-white/10 text-white/40 text-xs leading-relaxed font-sans">
            This note is provided for informational purposes only and does not constitute legal advice.
            It reflects the general framework as of the publication date and may not account for subsequent
            statutory or case-law developments. For a case-specific assessment, submit your file for a
            written preliminary review.
          </div>

          {/* CTA */}
          <FadeUp delay={0.1}>
            <div className="mt-16 border border-white/10 p-8 md:p-10 bg-ink-900">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">— Have a file?</div>
              <h3 className="font-serif text-3xl md:text-4xl text-white font-light leading-tight mb-4">
                Submit it for a written review.
              </h3>
              <p className="text-white/70 mb-8">
                Free preliminary feasibility memo. Response within 48 working hours. No obligation to proceed.
              </p>
              <Link to="/#claim-form" data-testid="article-cta"
                className="inline-flex items-center gap-3 bg-white text-black px-6 py-3 text-[11px] font-mono uppercase tracking-[0.25em] hover:bg-oxblood hover:text-white transition-colors duration-300">
                Submit Your Claim for Review →
              </Link>
              {COUNTRY_PATH[article.country] && (
                <div className="mt-6">
                  <Link to={COUNTRY_PATH[article.country]} data-testid="article-country-link"
                    className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50 hover:text-white transition-colors">
                    Debt recovery in Italy for {article.country} businesses →
                  </Link>
                </div>
              )}
            </div>
          </FadeUp>
        </div>
      </article>

      {/* Related */}
      <section className="border-t border-white/10 py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-8">— Related notes</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {related.map((r) => (
              <Link key={r.slug} to={`/articles/${r.slug}`} data-testid={`related-${r.slug}`}
                className="block bg-ink-900 p-8 group hover:bg-ink-800 transition-colors">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">— {r.country}</div>
                <h3 className="font-serif text-2xl text-white leading-tight mb-3">{r.title}</h3>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 group-hover:text-white transition-colors">Read →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
